// src/pages/Dashboard.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { documentService } from '../services/documentService';
import { folderService } from '../services/folderService';
import {
  Send, FileText, Users, FileCheck,
  Settings, LogOut, Plus, Search, Filter, Clock, ChevronRight, Zap, AlertTriangle,
  Flame, FileSignature, Brain
} from 'lucide-react';
import DocumentList from '../components/DocumentList';
import { useQuery, useQueryClient } from 'react-query';
import debounce from 'lodash/debounce';
import { useDocumentCache, documentCacheService } from '../services/documentCacheService';
import FolderView from '../components/FolderView';
import GitHubStyleFileList from '../components/GitHubStyleFileList';
import Breadcrumbs from '../components/Breadcrumbs';
import ViewToggle from '../components/ViewToggle';
import OnboardingModal from '../components/onboarding/OnboardingModal';
import DefaultSignatureDashboard from '../components/DefaultSignatureDashboard';
import PaymentRequiredModal from '../components/PaymentRequiredModal';
import { requiresPayment, getPaymentStatusMessage, hasFreeTrial, getRemainingFreeTrialDocuments } from '../utils/paymentUtils';

// Accessibility: Respect prefers-reduced-motion
const reducedMotionStyles = `
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      transform: none !important;
    }
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

// Inject styles for reduced motion support
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = reducedMotionStyles;
  if (!document.head.querySelector('style[data-reduced-motion]')) {
    styleElement.setAttribute('data-reduced-motion', 'true');
    document.head.appendChild(styleElement);
  }
}

// Apple-inspired design system from GitHubStyleFileList
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
  successLight: 'rgba(52, 199, 89, 0.1)',
  warningLight: 'rgba(255, 149, 0, 0.1)',
  errorLight: 'rgba(255, 59, 48, 0.1)',
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

// 🔄 REMOVED MODULE-LEVEL CACHE - React Query handles all caching now

const Dashboard = () => {
  const { user, logout, needsOnboarding, completeOnboarding, incrementOnboardingViewCount, refreshUserData } = useAuth();
  
  // Refresh user data on mount to get latest free trial fields
  useEffect(() => {
    if (user) {
      refreshUserData();
    }
  }, []); // Only run once on mount
  const { refreshPermissions } = usePermissions();
  const { startOnboarding, isOnboarding } = useOnboarding();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const documentCache = useDocumentCache();
  
  // Debug logging (reduced)
  // console.log('Dashboard - Current user:', user);
  // console.log('Dashboard - User ID:', user?.id);
  
  const [activeTab, setActiveTab] = useState('uploaded');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSidebarItem, setActiveSidebarItem] = useState('eSign');
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // Pagination state - traditional page-based approach
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(40); // 40 documents/folders per page
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // State for folders
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  // statusFilter drives both UI select and query key; values: 'all' | 'completed' | 'pending_signature' | 'draft'
  
  // View mode state - load from localStorage or default to grid
  const [viewMode, setViewMode] = useState(
    localStorage.getItem('signpact-view-mode') || 'grid'
  );
  
  // React Query hook for fetching documents with smart caching
  const { 
    data: documentsResponse = { documents: [], totalCount: 0 }, 
    isLoading, 
    error,
    refetch: refreshDocuments,
    isFetching
  } = useQuery(
    ['documents', activeTab, page, limit, searchQuery, statusFilter, currentFolderId],
    () => fetchDocuments(),
    {
      enabled: !!user?.id,
      keepPreviousData: true,
      refetchOnMount: 'always',
      refetchOnWindowFocus: statusFilter === 'pending_signature',
      refetchOnReconnect: statusFilter === 'pending_signature',
      staleTime: statusFilter === 'pending_signature' ? 30 * 1000 : 10 * 60 * 1000,
      cacheTime: statusFilter === 'pending_signature' ? 10 * 60 * 1000 : 60 * 60 * 1000,
      refetchInterval: statusFilter === 'pending_signature' ? 60 * 1000 : false,
      placeholderData: (prev) => prev,
      onSuccess: (response) => {
        const { documents = [], totalCount = 0 } = response || {};
        console.log('📥 Documents loaded:', documents.length, 'of', totalCount, 'total');
        
        // Track pending documents for selective updates
        if (documents && Array.isArray(documents)) {
          documents.forEach(doc => {
            if (doc.status === 'pending_signature') {
              documentCache.addPendingDocument(doc.id);
            }
          });
        }
        
        // Update pagination state
        setTotalCount(totalCount);
        setHasMore((page * limit) < totalCount);
      },
      onError: (error) => {
        console.error('❌ Dashboard documents fetch failed:', error);
      }
    }
  );

  // Extract documents and folders from response
  const documents = documentsResponse?.documents || [];
  const paginatedFolders = documentsResponse?.folders || [];

  // Calculate document composition for analytics (after documents are loaded)
  const documentComposition = useMemo(() => {
    const docsArray = documents || [];
    if (!Array.isArray(docsArray) || docsArray.length === 0) return {};
    
    const composition = docsArray.reduce((acc, doc) => {
      if (doc.status === 'completed') acc.completed++;
      else if (doc.status === 'pending_signature') acc.pending++;
      else if (doc.status === 'draft') acc.draft++;
      return acc;
    }, { completed: 0, pending: 0, draft: 0 });
    
    return composition;
  }, [documents]);

  // Opportunistic refresh when 'all' view contains pending items
  useEffect(() => {
    if (statusFilter === 'all') {
      const docsArray = documents || [];
      const hasPendingInAllView = Array.isArray(docsArray) && docsArray.some(d => d.status === 'pending_signature');
      if (hasPendingInAllView) {
        const id = setInterval(() => documentCache.refreshPendingDocuments(), 30 * 1000);
        return () => clearInterval(id);
      }
    }
    return undefined;
  }, [statusFilter, documents, documentCache]);

  // Live stats independent of current page (pending/completed/draft/total)
  const { data: statsData } = useQuery(
    ['documentStats', activeTab, currentFolderId, searchQuery],
    () => documentService.getDocumentStats(activeTab, currentFolderId, searchQuery),
    { enabled: !!user?.id, staleTime: 30 * 1000, refetchOnWindowFocus: true }
  );
  const statsPendingCount = statsData?.pendingCount ?? 0;
  const statsCompletedCount = statsData?.completedCount ?? 0;

  // Reset pagination when filters change
  useEffect(() => {
    setPage(1);
  }, [activeTab, searchQuery, statusFilter, currentFolderId]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);
  
  // Memoized helper functions using current page documents
  const countSignatureNeeded = useMemo(() => {
    const docsArray = documents || [];
    if (!Array.isArray(docsArray) || docsArray.length === 0) {
      return 0;
    }
    
    return docsArray.filter(doc => 
      doc.status === 'waiting_for_signature' || 
      doc.status === 'pending_signature'
    ).length;
  }, [documents]);
  
  const hasDocumentsNeedingSignature = useMemo(() => {
    const docsArray = documents || [];
    if (!Array.isArray(docsArray) || docsArray.length === 0) {
      return false;
    }
    
    return docsArray.some(doc => 
      doc.status === 'waiting_for_signature' || 
      doc.status === 'pending_signature'
    );
  }, [documents]);
  
  // Optimized document fetching function
  const fetchDocuments = async () => {
    const cacheKey = `docs-${activeTab}-${page}-${limit}-${searchQuery}-${statusFilter}-${currentFolderId || 'root'}`;

    try {
      // Call API with pagination, search, and filter parameters
      const data = await documentService.getUserDocuments(
        page, 
        limit, 
        activeTab, 
        searchQuery,
        statusFilter,
        'createdAt',
        'DESC',
        currentFolderId
      );
      
      // Extract documents and folders from the response
      const documentsArray = data.documents || [];
      const foldersArray = data.folders || [];
      
      // Avoid per-document waterfalls; rely on backend-provided fields
      let finalDocuments = documentsArray;
      
      // 🔄 REMOVED MODULE-LEVEL CACHE UPDATE - React Query handles caching
      
      // Return the full response with updated documents and folders
      return {
        ...data,
        documents: finalDocuments,
        folders: foldersArray
      };
    } catch (err) {
      console.error('Error fetching documents:', err);
      throw err;
    }
  };

  // Effect to reset pagination when tab changes
  useEffect(() => {
    setPage(1);
    setHasMore(true);
  }, [activeTab, searchQuery, statusFilter]);

  // Load folders on component mount and when currentFolderId changes
  useEffect(() => {
    const loadFolders = async () => {
      try {
        const foldersData = await folderService.getUserFolders(currentFolderId);
        setFolders(foldersData);
      } catch (error) {
        console.error('Error loading folders:', error);
      }
    };
    
    loadFolders();
  }, [currentFolderId]);

  // Onboarding logic
  useEffect(() => {
    console.log('🎯 Dashboard: Onboarding check:', {
      needsOnboarding,
      isOnboarding,
      userOnboardingCompleted: user?.onboardingCompleted,
      userOnboardingSteps: user?.onboardingSteps
    });
    
    if (needsOnboarding && !isOnboarding) {
      console.log('🚀 Dashboard: Starting onboarding flow...');
      
      // Refresh user data to ensure we have the latest information before onboarding
      refreshUserData()
        .then(() => {
          console.log('✅ Dashboard: User data refreshed for onboarding');
          // Increment view count and start onboarding
          return incrementOnboardingViewCount();
        })
        .then(() => {
          console.log('📊 Dashboard: Onboarding view count incremented');
          startOnboarding();
        })
        .catch((error) => {
          console.error('❌ Dashboard: Error in onboarding preparation:', error);
          // Start onboarding anyway, even if refresh or count increment fails
          startOnboarding();
        });
    }
  }, [needsOnboarding, isOnboarding, startOnboarding, incrementOnboardingViewCount, refreshUserData, user?.onboardingCompleted]);

  // Handle onboarding completion
  const handleOnboardingComplete = () => {
    console.log('🎉 Dashboard: Onboarding completed, updating state...');
    completeOnboarding();
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      setSearchQuery(query);
      setPage(1); // Reset to first page on new search
    }, 500),
    []
  );

  // Function to load more data for infinite scroll
  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setPage(prevPage => prevPage + 1);
      return Promise.resolve(); // Return a resolved promise
    }
    return Promise.resolve(); // Return a resolved promise if we're not loading more
  }, [isFetching, hasMore]);

  // Sidebar navigation items
  const sidebarItems = [
    { id: 'eSign', icon: FileSignature, label: 'eSign', active: activeSidebarItem === 'eSign' },
    { id: 'documents', icon: FileText, label: 'Documents', active: activeSidebarItem === 'documents' },
    { id: 'contacts', icon: Users, label: 'Contacts', active: activeSidebarItem === 'contacts' },
    { id: 'settings', icon: Settings, label: 'Settings', active: activeSidebarItem === 'settings' },
  ];

  const handleCreateEnvelope = () => {
    // Check if user has active payment before allowing document creation
    if (requiresPayment(user)) {
      setShowPaymentModal(true);
      return;
    }
    
    navigate('/new-document');
  };

  const handleSidebarItemClick = (itemId) => {
    setActiveSidebarItem(itemId);

    // Route changes based on sidebar selection
    switch (itemId) {
      case 'settings':
        navigate('/settings');
        break;
      case 'documents':
      case 'eSign':
      case 'contacts':
        // Stay on dashboard view for these sections
        navigate('/dashboard');
        break;
      default:
        break;
    }
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      // Use hideDocument instead of deleteDocument to work for both senders and recipients
      await documentService.hideDocument(documentId);
      
      // 🔄 Smart cache invalidation for document hiding
      documentCache.invalidateDocumentCache(documentId, 'hidden');
    } catch (error) {
      console.error('Error hiding document:', error);
      alert('Failed to hide document. Please try again.');
    }
  };

  const confirmDelete = (documentId, e) => {
    e.stopPropagation(); // Prevent row click event
    setDeletingDocumentId(documentId);
    setShowDeleteConfirm(true);
  };
  
  const handleConfirmDelete = async () => {
    if (deletingDocumentId) {
      await handleDeleteDocument(deletingDocumentId);
      setShowDeleteConfirm(false);
      setDeletingDocumentId(null);
    }
  };
  
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletingDocumentId(null);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      // Call the API to create the folder in the database
      const newFolder = await folderService.createFolder(newFolderName.trim(), currentFolderId);
      
      // Refresh the folders list from the backend to ensure we have the latest data
      const updatedFolders = await folderService.getUserFolders(currentFolderId);
      setFolders(updatedFolders);
      
      setShowNewFolderModal(false);
      setNewFolderName('');
    } catch (error) {
      console.error('Error creating folder:', error);
      alert('Failed to create folder. Please try again.');
    }
  };

  const handleCancelFolderCreation = () => {
    setShowNewFolderModal(false);
    setNewFolderName('');
  };

  const handleMoveDocument = async (documentId, targetFolderId) => {
    console.log(`Dashboard - Starting move: document ${documentId} to folder ${targetFolderId}`);
    
    // 🚀 IMMEDIATE OPTIMISTIC UPDATE: Remove document from view instantly
    const documentBeingMoved = documents.find(doc => doc.id === documentId);
    if (documentBeingMoved) {
      // Get the correct cache key that matches the current query
      const currentQueryKey = ['documents', activeTab, page, limit, searchQuery, statusFilter, currentFolderId];
      
      // Store original state for potential rollback
      const originalQueryData = queryClient.getQueryData(currentQueryKey);
      
      // Update React Query cache optimistically RIGHT NOW with the correct key
      queryClient.setQueryData(currentQueryKey, (oldData) => {
        if (!oldData) return oldData;
        
        // Remove the document from the current view if it's being moved to a different folder
        if (targetFolderId !== currentFolderId) {
          return oldData.filter(doc => doc.id !== documentId);
        }
        
        // If moving within the same folder, update the folderId
        return oldData.map(doc => 
          doc.id === documentId 
            ? { ...doc, folderId: targetFolderId }
            : doc
        );
      });
      
      console.log('🎯 Optimistic update applied - document disappeared immediately');
      
      // 🔄 Make API call in background (don't await)
      documentService.moveDocument(documentId, targetFolderId)
        .then(async (moveResult) => {
          console.log('Dashboard - Move API response:', moveResult);
          
          // 🔄 Invalidate React Query cache to ensure fresh data
          await queryClient.invalidateQueries(['documents']);
          
          // Force refresh the documents list to sync with backend
          console.log('Dashboard - Refreshing documents after move...');
          await refreshDocuments();
          
          console.log('Document moved successfully');
        })
        .catch(async (error) => {
          console.error('Error moving document:', error);
          
          // 🔄 ROLLBACK: Restore original state on error
          queryClient.setQueryData(currentQueryKey, originalQueryData);
          
          // Show error message to user
          alert(`Failed to move document "${documentBeingMoved.name}". The document has been restored to its original location.`);
          
          // Ensure we have fresh data
          await queryClient.invalidateQueries(['documents']);
          await refreshDocuments();
        });
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      // Remove from folders state
      setFolders(prevFolders => prevFolders.filter(folder => folder.id !== folderId));
      
      // TODO: Implement folder deletion API call
      console.log('Deleting folder', folderId);
      // await folderService.deleteFolder(folderId);
    } catch (error) {
      console.error('Error deleting folder:', error);
      alert('Failed to delete folder. Please try again.');
    }
  };

  const handleUploadSuccess = async (documentData) => {
    // 🔄 Smart cache invalidation for new document upload
    documentCache.invalidateDocumentCache(documentData.id, documentData.status || 'draft');
    
    // 🔄 Refresh permissions for the new document
    await refreshPermissions();
    
    // Force a refetch to update the UI immediately
    refreshDocuments().then(() => {
      console.log('Documents refreshed successfully after creation');
    }).catch(error => {
      console.error('Error refreshing documents after creation:', error);
    });
  };

  // Handle logout with proper cache clearing
  const handleLogout = async () => {
    try {
      // Clear all React Query caches to prevent data bleeding between users
      await queryClient.clear();
      
      // Clear document cache
      documentCache.clearAll?.();
      
      // Call the auth logout
      logout();
      
      console.log('✅ Successfully logged out and cleared all caches');
    } catch (error) {
      console.error('❌ Error during logout:', error);
      // Still logout even if cache clearing fails
      logout();
    }
  };

  // Handle navigation to signature field placement for existing documents
  const handleShowSignatureFields = (documentId, email, name) => {
    navigate('/new-document', {
      state: {
        documentId,
        recipientEmail: email,
        recipientName: name
      }
    });
  };



  return (
    <div className="min-h-screen bg-gray-50 flex" style={{fontFamily: fontStyles.fontFamily}}>
      {/* Sidebar */}
      <div 
        className="w-72 flex flex-col h-screen sticky top-0 shadow-xl"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 50%, rgba(241, 245, 249, 0.85) 100%)',
          backdropFilter: 'blur(20px) saturate(180%)',
          borderTopRightRadius: '24px',
          borderBottomRightRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.12), 0 10px 20px -5px rgba(0, 0, 0, 0.08)',
          borderRight: `1px solid rgba(148, 163, 184, 0.2)`,
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          position: 'sticky'
        }}
      >
        {/* Logo Section */}
        <div 
          className="p-6 border-b flex items-center justify-center transition-all duration-200 ease-out"
          style={{
            borderColor: 'rgba(148, 163, 184, 0.15)',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.7))',
            backdropFilter: 'blur(12px)',
            borderRadius: '0 24px 0 0',
            height: '88px'
          }}
        >
          <img src="/6.png" alt="SignPact Logo" className="h-10" />
        </div>
        
        <nav className="flex-1 p-6 overflow-y-auto">
          {/* Premium Action Button */}
          <div className="mb-8">
            <div 
              className="p-2 rounded-3xl border"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(248, 250, 252, 0.6))',
                borderColor: 'rgba(148, 163, 184, 0.15)',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
              }}
            >
              <button
                onClick={handleCreateEnvelope}
                className="w-full px-6 py-4 text-white flex items-center justify-center transition-all duration-300 ease-out font-semibold relative overflow-hidden group"
                style={{
                  borderRadius: '20px',
                  background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
                  boxShadow: '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                  ...fontStyles.medium,
                  letterSpacing: '-0.01em',
                  fontSize: '15px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px) scale(1.02)';
                  e.target.style.boxShadow = '0 12px 35px rgba(30, 64, 175, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                }}
              >
                {/* Shimmer effect */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%)',
                    animation: 'shimmer 2s infinite'
                  }}
                />
                <Plus size={18} className="mr-3 relative z-10" />
                <span className="relative z-10">New Document</span>
              </button>
            </div>
          </div>
          
          {/* Navigation Section Header */}
          <div 
            className="px-3 mb-4"
            style={{
              color: colors.gray500,
              fontSize: '11px',
              fontWeight: '600',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}
          >
            Workspace
          </div>
          
          {/* Enhanced Navigation */}
          <ul className="space-y-1 mb-8">
            <li>
              <button 
                onClick={() => handleSidebarItemClick('eSign')}
                className="w-full flex items-center px-4 py-3.5 transition-all duration-300 group relative"
                style={{
                  borderRadius: '16px',
                  backgroundColor: activeSidebarItem === 'eSign' 
                    ? 'rgba(30, 64, 175, 0.08)' 
                    : 'transparent',
                  color: activeSidebarItem === 'eSign' 
                    ? colors.accent 
                    : colors.gray700,
                  ...fontStyles.medium,
                  letterSpacing: '-0.01em',
                  fontSize: '15px',
                  border: activeSidebarItem === 'eSign' 
                    ? `1px solid rgba(30, 64, 175, 0.12)` 
                    : '1px solid transparent',
                  boxShadow: activeSidebarItem === 'eSign' 
                    ? 'inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 1px 3px rgba(30, 64, 175, 0.1)' 
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeSidebarItem !== 'eSign') {
                    e.target.style.backgroundColor = 'rgba(248, 250, 252, 0.7)';
                    e.target.style.transform = 'translateX(4px)';
                    e.target.style.color = colors.gray900;
                    e.target.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSidebarItem !== 'eSign') {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'translateX(0)';
                    e.target.style.color = colors.gray700;
                    e.target.style.borderColor = 'transparent';
                  }
                }}
              >
                <FileSignature className="mr-4" size={19} />
                <span className="flex-1 text-left">Signatures</span>
                <div className="flex items-center space-x-2">
                  {countSignatureNeeded > 0 && (
                    <span 
                      className="flex items-center justify-center text-white rounded-full h-5 w-5 text-xs font-semibold"
                      style={{
                        backgroundColor: colors.error,
                        fontSize: '11px',
                        boxShadow: `0 2px 8px rgba(239, 68, 68, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)`
                      }}
                    >
                      {countSignatureNeeded}
                    </span>
                  )}
                  {activeSidebarItem === 'eSign' && (
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: colors.accent,
                        boxShadow: `0 0 8px ${colors.accent}40`
                      }}
                    />
                  )}
                </div>
              </button>
            </li>
            <li>
              <button 
                onClick={() => navigate('/contacts')}
                className="w-full flex items-center px-4 py-3.5 transition-all duration-300 group relative"
                style={{
                  borderRadius: '16px',
                  backgroundColor: activeSidebarItem === 'contacts' 
                    ? 'rgba(30, 64, 175, 0.08)' 
                    : 'transparent',
                  color: activeSidebarItem === 'contacts' 
                    ? colors.accent 
                    : colors.gray700,
                  ...fontStyles.medium,
                  letterSpacing: '-0.01em',
                  fontSize: '15px',
                  border: activeSidebarItem === 'contacts' 
                    ? `1px solid rgba(30, 64, 175, 0.12)` 
                    : '1px solid transparent',
                  boxShadow: activeSidebarItem === 'contacts' 
                    ? 'inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 1px 3px rgba(30, 64, 175, 0.1)' 
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeSidebarItem !== 'contacts') {
                    e.target.style.backgroundColor = 'rgba(248, 250, 252, 0.7)';
                    e.target.style.transform = 'translateX(4px)';
                    e.target.style.color = colors.gray900;
                    e.target.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSidebarItem !== 'contacts') {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'translateX(0)';
                    e.target.style.color = colors.gray700;
                    e.target.style.borderColor = 'transparent';
                  }
                }}
              >
                <Users className="mr-4" size={19} />
                <span className="flex-1 text-left">Contacts</span>
                {activeSidebarItem === 'contacts' && (
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: colors.accent,
                      boxShadow: `0 0 8px ${colors.accent}40`
                    }}
                  />
                )}
              </button>
            </li>

            {/* Training Menu Item removed */}
            <li>
              <button 
                onClick={() => handleSidebarItemClick('settings')}
                className="w-full flex items-center px-4 py-3.5 transition-all duration-300 group relative"
                style={{
                  borderRadius: '16px',
                  backgroundColor: activeSidebarItem === 'settings' 
                    ? 'rgba(30, 64, 175, 0.08)' 
                    : 'transparent',
                  color: activeSidebarItem === 'settings' 
                    ? colors.accent 
                    : colors.gray700,
                  ...fontStyles.medium,
                  letterSpacing: '-0.01em',
                  fontSize: '15px',
                  border: activeSidebarItem === 'settings' 
                    ? `1px solid rgba(30, 64, 175, 0.12)` 
                    : '1px solid transparent',
                  boxShadow: activeSidebarItem === 'settings' 
                    ? 'inset 0 1px 0 rgba(255, 255, 255, 0.8), 0 1px 3px rgba(30, 64, 175, 0.1)' 
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeSidebarItem !== 'settings') {
                    e.target.style.backgroundColor = 'rgba(248, 250, 252, 0.7)';
                    e.target.style.transform = 'translateX(4px)';
                    e.target.style.color = colors.gray900;
                    e.target.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSidebarItem !== 'settings') {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.transform = 'translateX(0)';
                    e.target.style.color = colors.gray700;
                    e.target.style.borderColor = 'transparent';
                  }
                }}
              >
                <Settings className="mr-4" size={19} />
                <span className="flex-1 text-left">Settings</span>
                {activeSidebarItem === 'settings' && (
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: colors.accent,
                      boxShadow: `0 0 8px ${colors.accent}40`
                    }}
                  />
                )}
              </button>
            </li>
          </ul>
        </nav>
        
        {/* Enhanced User Section */}
        <div 
          className="p-6 border-t"
          style={{
            borderColor: 'rgba(148, 163, 184, 0.15)',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.7))',
            backdropFilter: 'blur(12px)',
            borderRadius: '0 0 24px 0'
          }}
        >
          {/* User Profile Card */}
          <div 
            className="p-5 mb-4 rounded-2xl border transition-all duration-300 ease-out cursor-pointer group relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.8))',
              borderColor: 'rgba(148, 163, 184, 0.2)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
            }}
            onClick={() => setShowProfileModal(true)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
              e.currentTarget.style.borderColor = 'rgba(30, 64, 175, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
              e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.2)';
            }}
          >
            {/* Subtle gradient overlay */}
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: `linear-gradient(135deg, ${colors.accent}05 0%, transparent 50%)`,
                pointerEvents: 'none'
              }}
            />
            
            <div className="flex items-center space-x-4 relative z-10">
              <div className="relative">
                <div 
                  className="p-0.5 rounded-full"
                  style={{
                    background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
                    boxShadow: '0 4px 12px rgba(30, 64, 175, 0.2)'
                  }}
                >
                  <img 
                    src={user?.picture || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(user?.name || 'User')} 
                    alt={user?.name} 
                    className="w-11 h-11 rounded-full border-2 border-white"
                  />
                </div>
                <div 
                  className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white"
                  style={{
                    backgroundColor: colors.success,
                    boxShadow: '0 2px 4px rgba(52, 199, 89, 0.3)'
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div 
                  className="font-semibold truncate"
                  style={{
                    color: colors.gray900,
                    fontSize: '15px',
                    ...fontStyles.medium,
                    letterSpacing: '-0.01em'
                  }}
                >
                  {user?.name}
                </div>
                <div 
                  className="text-xs truncate"
                  style={{
                    color: colors.gray500,
                    ...fontStyles.body,
                    letterSpacing: '-0.005em'
                  }}
                >
                  {user?.email}
                </div>
                <div 
                  className="text-xs mt-0.5"
                  style={{
                    color: colors.success,
                    fontSize: '11px',
                    fontWeight: '500'
                  }}
                >
                  • Online
                </div>
              </div>
            </div>
          </div>
          
          {/* Elegant Logout Button */}
          <button 
            onClick={handleLogout} 
            className="w-full flex items-center justify-center px-5 py-3.5 rounded-2xl font-medium transition-all duration-300 ease-out group relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))',
              color: colors.gray700,
              border: `1px solid rgba(148, 163, 184, 0.2)`,
              ...fontStyles.medium,
              letterSpacing: '-0.01em',
              fontSize: '14px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(135deg, rgba(248, 250, 252, 0.9), rgba(241, 245, 249, 0.8))';
              e.target.style.color = colors.gray900;
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
              e.target.style.borderColor = 'rgba(148, 163, 184, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))';
              e.target.style.color = colors.gray700;
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
              e.target.style.borderColor = 'rgba(148, 163, 184, 0.2)';
            }}
          >
            <LogOut size={16} className="mr-3 transition-transform duration-300 group-hover:-translate-x-0.5" />
            Sign Out
          </button>
        </div>
      </div>
      
      {/* Main content area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header 
          className="border-b py-4"
          style={{
            background: 'linear-gradient(to bottom, #f9f9f9, #f3f3f3)',
            borderColor: colors.gray200,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            height: '88px'
          }}
        >
          <div className="px-8 flex justify-between items-center h-full">
            <div className="flex items-center space-x-4">
              <h1 
                className="text-3xl font-bold"
                style={{
                  color: colors.gray900,
                  ...fontStyles.heading,
                  letterSpacing: '-0.03em',
                  lineHeight: '1.2'
                }}
              >
                {activeSidebarItem}
              </h1>
              <div 
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: colors.accentLight,
                  color: colors.accent,
                  ...fontStyles.medium
                }}
              >
                Dashboard
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="relative group">
                <input
                  type="text"
                  placeholder="Search documents..."
                  onChange={(e) => debouncedSearch(e.target.value)}
                  className="px-4 py-3 pl-11 text-sm transition-all duration-200 ease-out focus:outline-none w-72"
                  style={{
                    backgroundColor: colors.white,
                    border: `1px solid ${colors.gray200}`,
                    borderRadius: '12px',
                    color: colors.gray900,
                    ...fontStyles.body,
                    letterSpacing: '-0.01em',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    height: '44px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.accent;
                    e.target.style.boxShadow = `0 0 0 3px ${colors.accentLight}, 0 4px 12px rgba(0, 0, 0, 0.08)`;
                    e.target.nextSibling.style.color = colors.accent;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.gray200;
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                    e.target.nextSibling.style.color = colors.gray500;
                  }}
                />
                <Search 
                  size={16} 
                  className="absolute left-3.5 top-1/2 transform -translate-y-1/2 transition-all duration-200 ease-out pointer-events-none"
                  style={{color: colors.gray500}}
                />
                
                {/* Search suggestions dropdown - simplified */}
                <div 
                  className="absolute top-full left-0 right-0 mt-1 rounded-lg border shadow-lg opacity-0 invisible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200 ease-out z-50"
                  style={{
                    backgroundColor: colors.white,
                    borderColor: colors.gray200,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <div className="p-3">
                    <div 
                      className="text-xs mb-2"
                      style={{
                        color: colors.gray500,
                        ...fontStyles.medium
                      }}
                    >
                      Recent searches
                    </div>
                    <div className="space-y-1">
                      <div 
                        className="px-2 py-1.5 rounded-md transition-colors cursor-pointer text-sm"
                        style={{
                          color: colors.gray700,
                          ...fontStyles.body
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = colors.gray50;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                        }}
                      >
                        Contract documents
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>
        
        {/* Main content */}
        <div className="flex-1">
          <div className="max-w-7xl mx-auto px-8 py-6">

          {/* Onboarding Status Banner */}
          {user?.onboardingCompleted && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-green-800">Setup Complete!</h3>
                  <p className="text-sm text-green-600">
                    Your profile is set up. You can update your settings anytime.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/settings?tab=onboarding')}
                className="text-sm text-green-700 hover:text-green-800 font-medium underline"
              >
                View Settings
              </button>
            </div>
          )}

          {/* Free Trial Status Banner */}
          {hasFreeTrial(user) && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-blue-800">Free Trial Active</h3>
                  <p className="text-sm text-blue-600">
                    You have {getRemainingFreeTrialDocuments(user)} free document{getRemainingFreeTrialDocuments(user) === 1 ? '' : 's'} remaining. 
                    {getRemainingFreeTrialDocuments(user) === 1 && ' This is your last one!'} Upgrade to SignPact Pro for unlimited e-signatures + AI contract templates!
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/settings?tab=billing')}
                className="text-sm text-blue-700 hover:text-blue-800 font-medium underline"
              >
                Get SignPact Pro
              </button>
            </div>
          )}

          {/* Modernized Stats Cards */}
          {activeSidebarItem === 'eSign' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
              {/* Default Signature Card */}
              <DefaultSignatureDashboard />
              
              <div 
                className="p-6 rounded-3xl border transition-all duration-200 ease-out cursor-pointer group"
                style={{
                  background: countSignatureNeeded > 0 
                    ? 'linear-gradient(135deg, rgba(255, 149, 0, 0.05), rgba(255, 59, 48, 0.02))' 
                    : 'linear-gradient(135deg, rgba(52, 199, 89, 0.05), rgba(30, 64, 175, 0.02))',
                  borderColor: colors.gray200,
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)',
                  borderRadius: '24px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 0, 0, 0.1), 0 6px 15px rgba(0, 0, 0, 0.07)';
                  e.currentTarget.style.borderColor = countSignatureNeeded > 0 ? colors.warning : colors.success;
                  e.currentTarget.style.filter = 'brightness(1.01)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.borderColor = colors.gray200;
                  e.currentTarget.style.filter = 'brightness(1)';
                }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <p 
                      className="text-sm font-medium mb-2"
                      style={{
                        color: colors.gray500,
                        ...fontStyles.medium,
                        letterSpacing: '-0.005em'
                      }}
                    >
                      Waiting Signatures
                    </p>
                    <div className="flex items-baseline space-x-2">
                      <h3 
                        className="text-4xl font-bold transition-all duration-300"
                        style={{
                          color: colors.gray900,
                          ...fontStyles.heading,
                          letterSpacing: '-0.02em'
                        }}
                      >
                        {statsPendingCount || countSignatureNeeded}
                      </h3>
                      <div 
                        className="flex items-center text-xs font-medium px-2 py-1 rounded-full"
                        style={{
                          backgroundColor: countSignatureNeeded > 0 ? colors.warningLight : colors.successLight,
                          color: countSignatureNeeded > 0 ? colors.warning : colors.success
                        }}
                      >
                        {countSignatureNeeded > 0 ? "↗ Urgent" : "✓ Clear"}
                  </div>
                  </div>
                </div>
                  <div 
                    className="p-4 rounded-2xl transition-all duration-300"
                    style={{
                      background: countSignatureNeeded > 0 
                        ? `linear-gradient(135deg, ${colors.warningLight}, rgba(255, 149, 0, 0.15))` 
                        : `linear-gradient(135deg, ${colors.successLight}, rgba(52, 199, 89, 0.15))`,
                      boxShadow: countSignatureNeeded > 0 
                        ? `0 8px 20px ${colors.warning}20` 
                        : `0 8px 20px ${colors.success}20`
                    }}
                  >
                    <Clock 
                      size={24} 
                      className="transition-all duration-300"
                      style={{color: countSignatureNeeded > 0 ? colors.warning : colors.success}}
                    />
                  </div>
                </div>
                <div 
                  className="text-sm flex items-center justify-between pt-4 border-t"
                  style={{
                    borderColor: colors.gray100,
                    color: colors.gray600,
                    ...fontStyles.body
                  }}
                >
                  <span 
                    className="font-medium"
                    style={{
                      color: countSignatureNeeded > 0 ? colors.error : colors.success,
                      ...fontStyles.medium
                    }}
                  >
                    {countSignatureNeeded > 0 ? "Action needed" : "All caught up"}
                  </span>
                  <div 
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{
                      backgroundColor: countSignatureNeeded > 0 ? colors.error : colors.success,
                      boxShadow: countSignatureNeeded > 0 
                        ? `0 0 8px ${colors.error}60` 
                        : `0 0 8px ${colors.success}60`
                    }}
                  />
                </div>
              </div>
              
              <div 
                className="p-6 rounded-3xl border transition-all duration-200 ease-out cursor-pointer group"
                style={{
                  background: 'linear-gradient(135deg, rgba(52, 199, 89, 0.05), rgba(30, 64, 175, 0.02))',
                  borderColor: colors.gray200,
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)',
                  borderRadius: '24px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(0, 0, 0, 0.1), 0 6px 15px rgba(0, 0, 0, 0.07)';
                  e.currentTarget.style.borderColor = colors.success;
                  e.currentTarget.style.filter = 'brightness(1.01)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)';
                  e.currentTarget.style.borderColor = colors.gray200;
                  e.currentTarget.style.filter = 'brightness(1)';
                }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <p 
                      className="text-sm font-medium mb-2"
                      style={{
                        color: colors.gray500,
                        ...fontStyles.medium,
                        letterSpacing: '-0.005em'
                      }}
                    >
                      Completed
                    </p>
                    <div className="flex items-baseline space-x-2">
                      <h3 
                        className="text-4xl font-bold transition-all duration-300"
                        style={{
                          color: colors.gray900,
                          ...fontStyles.heading,
                          letterSpacing: '-0.02em'
                        }}
                      >
                      {statsCompletedCount}
                    </h3>
                      <div 
                        className="flex items-center text-xs font-medium px-2 py-1 rounded-full"
                        style={{
                          backgroundColor: colors.successLight,
                          color: colors.success
                        }}
                      >
                        ✓ Done
                  </div>
                  </div>
                </div>
                  <div 
                    className="p-4 rounded-2xl transition-all duration-300"
                    style={{
                      background: `linear-gradient(135deg, ${colors.successLight}, rgba(52, 199, 89, 0.15))`,
                      boxShadow: `0 8px 20px ${colors.success}20`
                    }}
                  >
                    <FileCheck 
                      size={24} 
                      className="transition-all duration-300"
                      style={{color: colors.success}}
                    />
                  </div>
                </div>
                <div 
                  className="text-sm flex items-center justify-between pt-4 border-t"
                  style={{
                    borderColor: colors.gray100,
                    color: colors.gray600,
                    ...fontStyles.body
                  }}
                >
                  <span 
                    className="font-medium"
                    style={{
                      color: colors.gray500,
                      ...fontStyles.medium
                    }}
                  >
                    Total completed documents
                  </span>
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: colors.success,
                      boxShadow: `0 0 8px ${colors.success}40`
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Balanced Controls Layout */}
          <div className="space-y-4 mb-8">
            {/* Primary Controls Row */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Premium Tab Navigation */}
              <div 
                className="relative p-1 border rounded-xl transition-all duration-300"
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(249, 249, 249, 0.8))',
                  borderColor: colors.gray200,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
                  backdropFilter: 'blur(8px)',
                  minWidth: '320px'
                }}
              >
                {/* Sliding Background Indicator */}
                <div
                  className="absolute top-1 bottom-1 rounded-[10px] transition-all duration-300 ease-out"
                  style={{
                    left: activeTab === 'uploaded' ? '4px' : '50%',
                    width: activeTab === 'uploaded' ? 'calc(50% - 6px)' : 'calc(50% - 6px)',
                    background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
                    boxShadow: `0 2px 8px rgba(30, 64, 175, 0.2)`,
                    zIndex: 1
                  }}
                />
                
                <div className="relative flex z-10">
                  <button
                    className="flex-1 px-4 py-2.5 text-sm font-medium transition-all duration-300 ease-out flex items-center justify-center relative rounded-[10px]"
                    onClick={() => setActiveTab('uploaded')}
                    style={{
                      color: activeTab === 'uploaded' ? colors.white : colors.gray700,
                      ...fontStyles.medium,
                      letterSpacing: '-0.01em',
                      textShadow: activeTab === 'uploaded' ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (activeTab !== 'uploaded') {
                        e.target.style.color = colors.gray900;
                        e.target.style.transform = 'scale(1.01)';
                      }
                    }}
                    
                    onMouseLeave={(e) => {
                      if (activeTab !== 'uploaded') {
                        e.target.style.color = colors.gray700;
                        e.target.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    <span>My Documents</span>
                    {activeTab !== 'uploaded' && hasDocumentsNeedingSignature && (
                      <div 
                        className="ml-2 w-2 h-2 rounded-full animate-pulse"
                        style={{
                          backgroundColor: colors.error,
                          boxShadow: `0 0 6px ${colors.error}60`
                        }}
                      />
                    )}
                  </button>
                  
                  <button
                    className="flex-1 px-4 py-2.5 text-sm font-medium transition-all duration-300 ease-out flex items-center justify-center relative rounded-[10px]"
                    onClick={() => setActiveTab('shared')}
                    style={{
                      color: activeTab === 'shared' ? colors.white : colors.gray700,
                      ...fontStyles.medium,
                      letterSpacing: '-0.01em',
                      textShadow: activeTab === 'shared' ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (activeTab !== 'shared') {
                        e.target.style.color = colors.gray900;
                        e.target.style.transform = 'scale(1.01)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeTab !== 'shared') {
                        e.target.style.color = colors.gray700;
                        e.target.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    <span>Shared With Me</span>
                    {activeTab !== 'shared' && hasDocumentsNeedingSignature && (
                      <div 
                        className="ml-2 w-2 h-2 rounded-full animate-pulse"
                        style={{
                          backgroundColor: colors.error,
                          boxShadow: `0 0 6px ${colors.error}60`
                        }}
                      />
                    )}
                  </button>
                </div>
              </div>

              {/* Action Controls Group */}
              <div className="flex items-center gap-3">
                {/* New Folder Button - Balanced Size */}
                <button 
                  onClick={() => setShowNewFolderModal(true)}
                  className="flex items-center px-4 py-2.5 rounded-xl font-medium transition-all duration-300 ease-out border group"
                  style={{
                    background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
                    color: colors.white,
                    borderColor: colors.accent,
                    ...fontStyles.medium,
                    letterSpacing: '-0.01em',
                    boxShadow: '0 3px 12px rgba(30, 64, 175, 0.2)',
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                    height: '42px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(30, 64, 175, 0.25)';
                    e.target.style.filter = 'brightness(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 3px 12px rgba(30, 64, 175, 0.2)';
                    e.target.style.filter = 'brightness(1)';
                  }}
                  onMouseDown={(e) => {
                    e.target.style.transform = 'translateY(0) scale(0.98)';
                  }}
                  onMouseUp={(e) => {
                    e.target.style.transform = 'translateY(-1px) scale(1)';
                  }}
                >
                  <Plus 
                    size={16} 
                    className="mr-2 transition-transform duration-300 group-hover:rotate-90" 
                  />
                  New Folder
                </button>

                {/* Status Filter - Consistent Size */}
                <div className="relative">
                  <select 
                    className="px-4 py-2.5 rounded-xl font-medium focus:outline-none transition-all duration-300 ease-out cursor-pointer border appearance-none pr-10 bg-white"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{
                      color: colors.gray700,
                      backgroundColor: colors.white,
                      borderColor: colors.gray200,
                      ...fontStyles.medium,
                      letterSpacing: '-0.01em',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(249, 249, 249, 0.8))',
                      backdropFilter: 'blur(4px)',
                      minWidth: '130px',
                      height: '42px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = colors.accent;
                      e.target.style.boxShadow = `0 0 0 3px ${colors.accentLight}, 0 4px 12px rgba(0, 0, 0, 0.06)`;
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = colors.gray200;
                      e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = colors.gray300;
                      e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.06)';
                    }}
                    onMouseLeave={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.borderColor = colors.gray200;
                        e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                      }
                    }}
                  >
                    <option value="all">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="pending_signature">Pending Signature</option>
                    <option value="draft">Draft</option>
                    <option value="voided">Voided</option>
                  </select>
                  <div 
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none transition-transform duration-200"
                    style={{color: colors.gray500}}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary Controls Row */}
            <div className="flex items-center justify-between">
              {/* View Toggle */}
              <div className="hidden md:block">
                <ViewToggle 
                  viewMode={viewMode} 
                  onViewModeChange={setViewMode} 
                />
              </div>
              
              {/* Optional: Future controls can go here */}
              <div className="flex items-center space-x-3">
                {/* Space for sort controls, etc. */}
              </div>
            </div>
          </div>
          
          {/* Error message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start">
              <AlertTriangle size={20} className="text-red-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-red-800">Error loading documents</h3>
                <p className="text-red-600 text-sm">{error.message || 'Failed to load documents. Please try again.'}</p>
              </div>
            </div>
          )}
          
          {/* Breadcrumbs */}
          <Breadcrumbs 
            folders={folders}
            currentFolderId={currentFolderId}
            onFolderSelect={setCurrentFolderId}
            onMoveDocument={handleMoveDocument}
          />
          
          {/* Main GitHub-style folder/document layout */}
          <GitHubStyleFileList
            documents={documents}
            folders={paginatedFolders}
            isLoading={isLoading}
            error={error}
            activeTab={activeTab}
            userId={user?.id}
            currentFolderId={currentFolderId}
            onFolderSelect={setCurrentFolderId}
            onDeleteDocument={confirmDelete}
            onMoveDocument={handleMoveDocument}
            onDeleteFolder={handleDeleteFolder}
            onShowSignatureFields={handleShowSignatureFields}
            statusFilter={statusFilter}
            viewMode={viewMode}
          />
          
          {/* Elegant Pagination Controls */}
          {totalCount > limit && (
            <div 
              className="mt-8 p-6 border rounded-2xl transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(249, 249, 249, 0.8))',
                borderColor: colors.gray200,
                backdropFilter: 'blur(12px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05)',
                borderRadius: '24px'
              }}
            >
              {/* Results Info Section */}
              <div className="flex items-center justify-center mb-6">
                <div className="flex items-center space-x-3">
                  <div 
                    className="flex items-center text-sm px-4 py-2 rounded-xl border"
                    style={{
                      color: colors.gray700,
                      backgroundColor: colors.white,
                      borderColor: colors.gray200,
                      ...fontStyles.medium,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.02)'
                    }}
                  >
                    <span className="mr-2">Showing</span>
                    <span 
                      className="font-semibold px-3 py-1 rounded-lg"
                      style={{
                        backgroundColor: colors.accentLight,
                        color: colors.accent,
                        fontSize: '13px'
                      }}
                    >
                      {((page - 1) * limit) + 1}-{Math.min(page * limit, totalCount)}
                    </span>
                    <span className="mx-2">of</span>
                    <span 
                      className="font-semibold px-3 py-1 rounded-lg"
                      style={{
                        backgroundColor: colors.gray100,
                        color: colors.gray700,
                        fontSize: '13px'
                      }}
                    >
                      {totalCount}
                    </span>
                    <span className="ml-2">results</span>
                  </div>
                  
                  {isFetching && (
                    <div className="flex items-center space-x-2 px-3 py-2 rounded-xl border" style={{
                      backgroundColor: colors.white,
                      borderColor: colors.gray200
                    }}>
                      <div 
                        className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                        style={{borderColor: `${colors.accent} transparent ${colors.accent} ${colors.accent}`}}
                      />
                      <span className="text-xs font-medium" style={{color: colors.gray600}}>Loading...</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Navigation Controls */}
              <div className="flex items-center justify-center">
                <div 
                  className="flex items-center p-1 border rounded-2xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.7))',
                    borderColor: colors.gray200,
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)'
                  }}
                >
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1 || isFetching}
                    className="px-4 py-2 text-sm font-medium transition-all duration-300 ease-out rounded-xl disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: page === 1 || isFetching ? 'transparent' : colors.white,
                      borderColor: 'transparent',
                      color: page === 1 || isFetching ? colors.gray400 : colors.gray700,
                      ...fontStyles.medium,
                      opacity: page === 1 || isFetching ? 0.5 : 1,
                      boxShadow: page === 1 || isFetching ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.04)'
                    }}
                    onMouseEnter={(e) => {
                      if (page !== 1 && !isFetching) {
                        e.target.style.backgroundColor = colors.gray50;
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (page !== 1 && !isFetching) {
                        e.target.style.backgroundColor = colors.white;
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.04)';
                      }
                    }}
                  >
                    First
                  </button>
                  
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1 || isFetching}
                    className="px-4 py-2 text-sm font-medium transition-all duration-300 ease-out rounded-xl disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: page === 1 || isFetching ? 'transparent' : colors.white,
                      borderColor: 'transparent',
                      color: page === 1 || isFetching ? colors.gray400 : colors.gray700,
                      ...fontStyles.medium,
                      opacity: page === 1 || isFetching ? 0.5 : 1,
                      boxShadow: page === 1 || isFetching ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.04)'
                    }}
                    onMouseEnter={(e) => {
                      if (page !== 1 && !isFetching) {
                        e.target.style.backgroundColor = colors.gray50;
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (page !== 1 && !isFetching) {
                        e.target.style.backgroundColor = colors.white;
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.04)';
                      }
                    }}
                  >
                    ←
                  </button>
                
                  {/* Page Numbers */}
                  <div className="flex items-center space-x-1 mx-2">
                    {Array.from({ length: Math.min(5, Math.ceil(totalCount / limit)) }, (_, i) => {
                      const startPage = Math.max(1, page - 2);
                      const pageNum = startPage + i;
                      const maxPage = Math.ceil(totalCount / limit);
                      
                      if (pageNum > maxPage) return null;
                      
                      const isCurrentPage = page === pageNum;
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          disabled={isFetching}
                          className="px-3 py-2 text-sm font-medium transition-all duration-300 ease-out rounded-xl min-w-[40px] disabled:cursor-not-allowed"
                          style={{
                            backgroundColor: isCurrentPage 
                              ? `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`
                              : isFetching 
                                ? 'transparent' 
                                : colors.white,
                            background: isCurrentPage 
                              ? `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`
                              : isFetching 
                                ? 'transparent' 
                                : colors.white,
                            borderColor: 'transparent',
                            color: isCurrentPage 
                              ? colors.white 
                              : isFetching 
                                ? colors.gray400 
                                : colors.gray700,
                            ...fontStyles.medium,
                            boxShadow: isCurrentPage 
                              ? `0 4px 12px ${colors.accent}40` 
                              : isFetching 
                                ? 'none'
                                : '0 2px 4px rgba(0, 0, 0, 0.04)',
                            opacity: isFetching && !isCurrentPage ? 0.5 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (!isCurrentPage && !isFetching) {
                              e.target.style.backgroundColor = colors.accentLight;
                              e.target.style.color = colors.accent;
                              e.target.style.transform = 'translateY(-1px)';
                              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isCurrentPage && !isFetching) {
                              e.target.style.backgroundColor = colors.white;
                              e.target.style.color = colors.gray700;
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.04)';
                            }
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={!hasMore || isFetching}
                    className="px-4 py-2 text-sm font-medium transition-all duration-300 ease-out rounded-xl disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: !hasMore || isFetching ? 'transparent' : colors.white,
                      borderColor: 'transparent',
                      color: !hasMore || isFetching ? colors.gray400 : colors.gray700,
                      ...fontStyles.medium,
                      opacity: !hasMore || isFetching ? 0.5 : 1,
                      boxShadow: !hasMore || isFetching ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.04)'
                    }}
                    onMouseEnter={(e) => {
                      if (hasMore && !isFetching) {
                        e.target.style.backgroundColor = colors.gray50;
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (hasMore && !isFetching) {
                        e.target.style.backgroundColor = colors.white;
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.04)';
                      }
                    }}
                  >
                    →
                  </button>
                  
                  <button
                    onClick={() => setPage(Math.ceil(totalCount / limit))}
                    disabled={!hasMore || isFetching}
                    className="px-4 py-2 text-sm font-medium transition-all duration-300 ease-out rounded-xl disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: !hasMore || isFetching ? 'transparent' : colors.white,
                      borderColor: 'transparent',
                      color: !hasMore || isFetching ? colors.gray400 : colors.gray700,
                      ...fontStyles.medium,
                      opacity: !hasMore || isFetching ? 0.5 : 1,
                      boxShadow: !hasMore || isFetching ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.04)'
                    }}
                    onMouseEnter={(e) => {
                      if (hasMore && !isFetching) {
                        e.target.style.backgroundColor = colors.gray50;
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (hasMore && !isFetching) {
                        e.target.style.backgroundColor = colors.white;
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.04)';
                      }
                    }}
                  >
                    Last
                  </button>
                </div>
              </div>
            </div>
          )}
          
          </div>
        </div>
      </div>
      
      {/* New Folder Modal */}
      {showNewFolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-md w-full mx-4 shadow-xl">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Folder</h2>
            <div className="mb-4">
              <label htmlFor="folderName" className="block text-sm font-medium text-gray-700 mb-2">
                Folder Name
              </label>
              <input
                type="text"
                id="folderName"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateFolder();
                  }
                }}
                placeholder="Enter folder name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#1e40af] focus:border-[#1e40af]"
                autoFocus
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelFolderCreation}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                className="px-4 py-2 bg-[#1e40af] text-white rounded-md hover:bg-[#1e3a8a] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-md w-full mx-4 shadow-xl">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Hide Document</h2>
            <p className="text-gray-700 mb-6">
              Are you sure you want to remove this document from your dashboard? The document will still be stored securely in our system for compliance purposes, but it will no longer appear in your list.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Required Modal */}
      {showPaymentModal && (
        <PaymentRequiredModal 
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          message={getPaymentStatusMessage(user).message}
          redirectPath={getPaymentStatusMessage(user).redirectPath}
          isFreeTrialMessage={getPaymentStatusMessage(user).isFreeTrialMessage}
        />
      )}

      {/* Onboarding Modal */}
      {isOnboarding && (
        <OnboardingModal 
          onComplete={handleOnboardingComplete}
          onClose={handleOnboardingComplete}
        />
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Profile Information</h2>
              <button
                onClick={() => setShowProfileModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-6">
              {/* Profile Picture and Basic Info */}
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div 
                    className="p-0.5 rounded-full"
                    style={{
                      background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
                      boxShadow: '0 4px 12px rgba(30, 64, 175, 0.2)'
                    }}
                  >
                    <img 
                      src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'User')}&background=3b82f6&color=fff`}
                      alt={user?.name} 
                      className="w-16 h-16 rounded-full border-2 border-white"
                    />
                  </div>
                  <div 
                    className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white"
                    style={{
                      backgroundColor: colors.success,
                      boxShadow: '0 2px 4px rgba(52, 199, 89, 0.3)'
                    }}
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{user?.name}</h3>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                  <span className="inline-block mt-1 text-xs text-green-600 font-medium">● Online</span>
                </div>
              </div>

              {/* Profile Details */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Job Title
                    </label>
                    <p className="text-sm text-gray-900">{user?.jobTitle || 'Not provided'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Company
                    </label>
                    <p className="text-sm text-gray-900">{user?.companyName || 'Not provided'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Phone Number
                    </label>
                    <p className="text-sm text-gray-900">{user?.phoneNumber || 'Not provided'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Use Case
                    </label>
                    <p className="text-sm text-gray-900">{user?.useCase || 'Not provided'}</p>
                  </div>
                </div>

                {/* Preferences Section */}
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Preferences</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email Notifications:</span>
                      <span className="text-gray-900">
                        {user?.userPreferences?.emailNotifications ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Signature Style:</span>
                      <span className="text-gray-900 capitalize">
                        {user?.userPreferences?.signatureStyle || 'draw'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Document Sharing:</span>
                      <span className="text-gray-900 capitalize">
                        {user?.userPreferences?.documentSharing || 'private'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Time Zone:</span>
                      <span className="text-gray-900">
                        {user?.userPreferences?.timeZone || 'UTC'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowProfileModal(false);
                    navigate('/settings');
                  }}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                >
                  Edit Profile
                </button>
                <button
                  onClick={() => setShowProfileModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default Dashboard;